import {
  Avatar,
  Box,
  Button,
  Checkbox,
  Chip,
  CircularProgress,
  FormControlLabel,
  Grid,
  InputAdornment,
  Menu,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import EmailOutlinedIcon from "@mui/icons-material/EmailOutlined";
import PhoneOutlinedIcon from "@mui/icons-material/PhoneOutlined";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CustomSelect from "../../../../../../components/ui/Select";
import { MemoryMessage } from "../hooks/useMemoryMessages";
import apiService from "../../../../../../service/Api";
import {
  showErrorToast,
  showSuccessToast,
} from "../../../../../../components/common/ShowToast";
import { Contacts, DeleteOutline, UploadFile } from "@mui/icons-material";

import { ImportContactsModal } from "./ImportContactsModal";

import { ContactsMemoryThankYouCard } from "./ContactsMemoryThankYouCard";
import { formatPhoneForWhatsApp } from "../../../utils/phone";
import { BulkWhatsAppModal } from "./BulkWhatsAppModal";

interface Contact {
  id: string; // stable unique key: lowercased email if present, otherwise a synthetic key
  email: string;
  name: string;
  phone: string;
  source: "generic" | "youtube" | "imported" | "visitor";
  count: number;
  contactId?: string;
  alreadySaved: boolean;
}

const isRealPhone = (value?: string | null): value is string => {
  if (!value) return false;
  const normalized = value.trim().toLowerCase();
  return normalized !== "" && normalized !== "n/a" && normalized !== "na";
};

const patchPhoneIfMissing = async (email: string, phone: string) => {
  if (!email || !isRealPhone(phone)) return;
  try {
    const searchRes = await apiService.get<{
      count: number;
      results: Array<{ id: string; phone: string | null }>;
    }>(`/api/contacts/contact/?email=${encodeURIComponent(email)}`);

    const existing = searchRes.data.results[0];
    if (existing && !isRealPhone(existing.phone)) {
      await apiService.patch(`/api/contacts/contact/${existing.id}/`, {
        phone,
      });
    }
  } catch {
    // Non-fatal: the contact was still created, just without the phone patch.
  }
};

interface SavedContact {
  id: number;
  first_name: string | null;
  last_name: string | null;
  email: string | null;
  phone: string | null;
}

export interface StreamVisitorRecord {
  id: number;
  first_name: string;
  last_name: string;
  email: string;
  phone_number: string;
}

interface ContactsTabProps {
  messages: MemoryMessage[];
  streamId: number | null;
  visitors?: StreamVisitorRecord[];
  // Needed to build the thank-you graphic + personalized message when
  // clicking the WhatsApp icon on an imported contact.
  deceasedName?: string;
  streamLink?: string;
  backgroundImage?: string;
  logoImage?: string;
}

function ContactCard({
  contact,
  checked,
  onCheck,
  converted,
  contactId,
  backendPhone,
  onConverted,
  onDeleted,
  streamId,
  thanked,
  onWhatsAppClick,
  thankYouMode,
  thankable,
  thankYouChecked,
  onThankYouCheck,
}: {
  contact: Contact;
  streamId: number | null;
  checked: boolean;
  onCheck: () => void;
  converted: boolean;
  contactId?: string;
  backendPhone?: string;
  onConverted: () => void;
  onDeleted: () => void;
  thanked: boolean;
  onWhatsAppClick: () => void;
  thankYouMode: boolean;
  thankable: boolean;
  thankYouChecked: boolean;
  onThankYouCheck: () => void;
}) {
  const [isConverting, setIsConverting] = useState(false);
  const [waMenuAnchor, setWaMenuAnchor] = useState<null | HTMLElement>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (!contactId) return;
    if (
      !window.confirm(
        `Delete ${contact.name || "this contact"}? This can't be undone.`,
      )
    ) {
      return;
    }
    setIsDeleting(true);
    try {
      await apiService.delete(`/api/contacts/contact/${contactId}/`);
      showSuccessToast("Contact deleted.");
      onDeleted();
    } catch {
      showErrorToast("Failed to delete contact.");
    } finally {
      setIsDeleting(false);
    }
  };

  const displayPhone = isRealPhone(backendPhone) ? backendPhone : contact.phone;

  const initials = (contact.name ?? "A")
    .split(" ")
    .map((n) => n.charAt(0))
    .slice(0, 2)
    .join("")
    .toUpperCase();

  const handleConvertSingle = async () => {
    if (converted) return; // already saved — never re-submit
    setIsConverting(true);
    try {
      const response = await apiService.post<{
        created: number;
        skipped: number;
        errors: string[];
      }>("/api/contacts/contact/bulk-create-from-comments/", {
        stream_id: streamId,
        comments: [
          {
            author: contact.name,
            email: contact.email || null,
            phone: contact.phone,
          },
        ],
      });

      if (response.data.created > 0) {
        await patchPhoneIfMissing(contact.email, contact.phone);
        showSuccessToast(`${contact.name} added to contacts.`);
      } else {
        showSuccessToast("Contact already exists, skipped.");
      }
      onConverted();
    } catch {
      showErrorToast("Failed to convert contact.");
    } finally {
      setIsConverting(false);
    }
  };

  const sourceLabel =
    contact.source === "youtube"
      ? "Youtube"
      : contact.source === "imported"
        ? "Imported"
        : contact.source === "visitor"
          ? "Visitor"
        : "Memorial Page";

  const sourceColor =
    contact.source === "youtube"
      ? "#ef4444"
      : contact.source === "imported"
        ? "#2563eb"
        : "#707070";

  const sourceBg =
    contact.source === "youtube"
      ? "#fef2f2"
      : contact.source === "imported"
        ? "#eff6ff"
        : "#e3e0e0";

  // Only imported contacts go through the thank-you-graphic + wa.me flow —
  // organic commenters are handled by the normal manual process for now.
  const useThankYouFlow = contact.source === "imported" && !!displayPhone;

  return (
    <Paper
      sx={{
        borderRadius: "12px",
        p: 2.5, // Increased internal structural breathing room
        boxShadow: 0,
        bgcolor: converted ? "#f0fdf4" : "background.paper",
        position: "relative",
        border: `1px solid ${converted ? "#12ad01" : "#f0f4f8"}`,
        transition: "all 0.2s ease-in-out",
        height: "100%",
        display: "flex",
        flexDirection: "column",
        justifyContent: "space-between",
        opacity: thankYouMode && !thankable ? 0.5 : 1,
      }}
    >
      <Box>
        {thankYouMode ? (
          thankable && (
            <Checkbox
              checked={thankYouChecked}
              onChange={onThankYouCheck}
              size="small"
              sx={{
                position: "absolute",
                top: 10,
                left: 10,
                p: 0,
                color: "#cbd5e1",
                "&.Mui-checked": { color: "#25D366" },
              }}
            />
          )
        ) : (
          <Checkbox
            checked={checked}
            onChange={onCheck}
            disabled={converted}
            size="small"
            sx={{
              position: "absolute",
              top: 10,
              left: 10,
              p: 0,
              color: "#cbd5e1",
              "&.Mui-checked": { color: "#724e41" },
            }}
          />
        )}

        <Tooltip
          title={
            converted && contactId
              ? "View contact"
              : converted
                ? "Already a contact"
                : "Convert to contact"
          }
        >
          <Box
            component={converted && contactId ? Link : "div"}
            to={
              converted && contactId
                ? `/contacts/individual/view/${contactId}`
                : undefined
            }
            onClick={
              !converted && !isConverting ? handleConvertSingle : undefined
            }
            sx={{
              position: "absolute",
              top: 10,
              right: 10,
              width: 28,
              height: 28,
              borderRadius: "50%",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: converted
                ? "default"
                : isConverting
                  ? "not-allowed"
                  : "pointer",
              "&:hover": {
                bgcolor: converted ? "transparent" : "action.hover",
              },
            }}
          >
            {isConverting ? (
              <CircularProgress size={14} />
            ) : (
              <Contacts
                sx={{ fontSize: 18, color: converted ? "#006838" : "#8E8E8E" }}
              />
            )}
          </Box>
        </Tooltip>

        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            pt: 1.5,
          }}
        >
          <Avatar
            sx={{
              width: 56,
              height: 56,
              bgcolor: converted ? "#006838" : "#8E8E8E",
              fontSize: "16px",
              fontWeight: 700,
              mb: 1.5,
            }}
          >
            {initials}
          </Avatar>

          <Typography
            component={converted && contactId ? Link : "span"}
            to={
              converted && contactId
                ? `/contacts/individual/view/${contactId}`
                : undefined
            }
            noWrap
            sx={{
              fontWeight: 600,
              cursor: converted && contactId ? "pointer" : "default",
              textDecoration: converted && contactId ? "underline" : "none",
              fontSize: "15px",
              color: "#1e293b",
              textAlign: "center",
              width: "100%",
              mb: 0.5,
            }}
          >
            {contact.name || "Anonymous"}
          </Typography>

          <Stack
            direction="row"
            spacing={0.5}
            alignItems="center"
            sx={{ mb: 2 }}
          >
            <Chip
              label={sourceLabel}
              size="small"
              sx={{
                bgcolor: sourceBg,
                color: sourceColor,
                fontWeight: 600,
                fontSize: "11px",
                height: 20,
                borderRadius: "4px",
              }}
            />
            {useThankYouFlow && thanked && (
              <Chip
                label="Thanked"
                size="small"
                sx={{
                  bgcolor: "#f0fdf4",
                  color: "#006838",
                  fontWeight: 600,
                  fontSize: "11px",
                  height: 20,
                  borderRadius: "4px",
                }}
              />
            )}
          </Stack>
        </Box>
      </Box>

      <Stack
        direction="row"
        spacing={2}
        justifyContent="center"
        sx={{ mt: "auto", pt: 1 }}
      >
        <Box
          component="a"
          href={
            displayPhone
              ? `tel:+${formatPhoneForWhatsApp(displayPhone)}`
              : undefined
          }
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: displayPhone ? "pointer" : "default",
            opacity: displayPhone ? 1 : 0.3,
          }}
        >
          <PhoneOutlinedIcon sx={{ fontSize: 22, color: "#8E8E8E" }} />
        </Box>
        <Box
          component="a"
          href={contact.email ? `mailto:${contact.email}` : undefined}
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: contact.email ? "pointer" : "default",
            opacity: contact.email ? 1 : 0.3,
          }}
        >
          <EmailOutlinedIcon sx={{ fontSize: 22, color: "#8E8E8E" }} />
        </Box>

        {/* Imported contacts: clicking WhatsApp opens the thank-you-graphic +
            pre-filled message flow instead of a bare wa.me link. */}
        {useThankYouFlow ? (
          <>
            <Tooltip
              title={
                thanked ? "Thanked — click for options" : "WhatsApp options"
              }
            >
              <Box
                component="button"
                onClick={(e) => setWaMenuAnchor(e.currentTarget)}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  border: "none",
                  background: "none",
                  p: 0,
                }}
              >
                <WhatsAppIcon
                  sx={{ fontSize: 22, color: thanked ? "#25D366" : "#8E8E8E" }}
                />
              </Box>
            </Tooltip>

            <Menu
              anchorEl={waMenuAnchor}
              open={!!waMenuAnchor}
              onClose={() => setWaMenuAnchor(null)}
            >
              <MenuItem
                onClick={() => {
                  setWaMenuAnchor(null);
                  window.open(
                    `https://wa.me/${formatPhoneForWhatsApp(displayPhone!)}`,
                    "_blank",
                    "noopener,noreferrer",
                  );
                }}
              >
                Send WhatsApp Message
              </MenuItem>
              <MenuItem
                onClick={() => {
                  setWaMenuAnchor(null);
                  onWhatsAppClick();
                }}
              >
                Send Thank You
              </MenuItem>
            </Menu>
          </>
        ) : (
          <Box
            component="a"
            href={
              contact.phone
                ? `https://wa.me/${formatPhoneForWhatsApp(contact.phone)}`
                : undefined
            }
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: displayPhone ? "pointer" : "default",
              opacity: displayPhone ? 1 : 0.3,
            }}
          >
            <WhatsAppIcon sx={{ fontSize: 22, color: "#8E8E8E" }} />
          </Box>
        )}
        {contactId && (
          <Tooltip title="Delete contact">
            <Box
              component="button"
              onClick={handleDelete}
              disabled={isDeleting}
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                cursor: isDeleting ? "not-allowed" : "pointer",
                border: "none",
                background: "none",
                p: 0,
              }}
            >
              {isDeleting ? (
                <CircularProgress size={18} />
              ) : (
                <DeleteOutline sx={{ fontSize: 22, color: "#ef4444" }} />
              )}
            </Box>
          </Tooltip>
        )}
      </Stack>
    </Paper>
  );
}

export default function ContactsTab({
  messages,
  streamId,
  visitors = [],
  deceasedName = "",
  streamLink = "",
}: ContactsTabProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [filterSource, setFilterSource] = useState("all");
  const [checkedIds, setCheckedIds] = useState<string[]>([]);
  const [isConverting, setIsConverting] = useState(false);
  const [existingEmails, setExistingEmails] = useState<Set<string>>(new Set());
  const [importOpen, setImportOpen] = useState(false);
  const [savedContacts, setSavedContacts] = useState<SavedContact[]>([]);
  const [thankedIds, setThankedIds] = useState<Set<string>>(new Set());

  // Bulk "thank you" selection mode — separate from the convert-to-contacts
  // checkboxes above, since eligibility rules differ (imported + has phone +
  // not already thanked) and most imported contacts are already "converted".
  const [thankYouMode, setThankYouMode] = useState(false);
  const [checkedThankYouIds, setCheckedThankYouIds] = useState<string[]>([]);
  const [bulkWhatsAppOpen, setBulkWhatsAppOpen] = useState(false);

  useEffect(() => {
    if (!streamId) return;
    try {
      const stored = localStorage.getItem(`thanked-contacts-${streamId}`);
      setThankedIds(stored ? new Set(JSON.parse(stored)) : new Set());
    } catch {
      setThankedIds(new Set());
    }
  }, [streamId]);
  const [thankYouTarget, setThankYouTarget] = useState<Contact | null>(null);

  // Contacts already imported for this memorial page: they may have no comment of
  // their own (handwritten tributes), so they would otherwise never get a card.
  const fetchSavedContacts = useCallback(() => {
    if (!streamId) return;
    apiService
      .get<{ results: SavedContact[] }>(
        `/api/contacts/contact/?from_stream=${streamId}&page_size=1000`,
      )
      .then((res) => setSavedContacts(res.data.results))
      .catch(() => {});
  }, [streamId]);

  useEffect(() => {
    fetchSavedContacts();
  }, [fetchSavedContacts]);

  const contacts = useMemo<Contact[]>(() => {
    const map = new Map<string, Contact>();

    messages.forEach((msg) => {
      const key = msg.sender_email?.trim().toLowerCase() || `no-email-${msg.id}`;
      if (map.has(key)) {
        map.get(key)!.count += 1;
      } else {
        map.set(key, {
          id: key,
          email: msg.sender_email ?? "",
          name: msg.sender_full_name ?? "Anonymous",
          phone: msg.sender_phone_number ?? "",
          source: msg.comment_source,
          count: 1,
          alreadySaved: false,
        });
      }
    });

    visitors.forEach((visitor) => {
      const key = visitor.email.trim().toLowerCase();
      const name = `${visitor.first_name} ${visitor.last_name}`.trim();
      const existing = map.get(key);

      if (existing) {
        if ((!existing.name || existing.name === "Anonymous") && name) {
          existing.name = name;
        }
        if (!isRealPhone(existing.phone) && isRealPhone(visitor.phone_number)) {
          existing.phone = visitor.phone_number;
        }
      } else {
        map.set(key, {
          id: key,
          email: visitor.email,
          name: name || "Anonymous",
          phone: visitor.phone_number,
          source: "visitor",
          count: 1,
          alreadySaved: false,
        });
      }
    });

    savedContacts.forEach((sc) => {
      const key = sc.email?.trim().toLowerCase() || `no-email-contact-${sc.id}`;
      const fullName = [sc.first_name, sc.last_name]
        .filter(Boolean)
        .join(" ")
        .trim();

      const existing = map.get(key);
      if (existing) {
        existing.alreadySaved = true;
        existing.contactId = sc.id.toString();
        if (isRealPhone(sc.phone) && !isRealPhone(existing.phone)) {
          existing.phone = sc.phone as string;
        }
      } else {
        map.set(key, {
          id: key,
          contactId: sc.id.toString(),
          email: sc.email ?? "",
          name: fullName || "Anonymous",
          phone: sc.phone ?? "",
          source: "imported",
          count: 1,
          alreadySaved: true,
        });
      }
    });

    return Array.from(map.values());
  }, [messages, savedContacts, visitors]);

  // Resolves the phone to actually use for a contact — backend-saved phone
  // wins over whatever came off the original comment, mirroring the display
  // logic already used per-card.

  const isThankable = useCallback(
    (contact: Contact) =>
      contact.source === "imported" &&
      isRealPhone(contact.phone) &&
      !thankedIds.has(contact.id),
    [thankedIds],
  );

  const filtered = useMemo(() => {
    return contacts.filter((c) => {
      const matchesSearch =
        c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        c.email.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesSource = filterSource === "all" || c.source === filterSource;
      return matchesSearch && matchesSource;
    });
  }, [contacts, searchQuery, filterSource]);

  const thankableFiltered = useMemo(
    () => filtered.filter(isThankable),
    [filtered, isThankable],
  );

  const isConverted = useCallback(
    (contact: Contact) =>
      contact.alreadySaved ||
      (!!contact.email && existingEmails.has(contact.email.toLowerCase())),
    [existingEmails],
  );

  const toggleCheck = (contact: Contact) => {
    if (isConverted(contact)) return;
    setCheckedIds((prev) =>
      prev.includes(contact.id)
        ? prev.filter((id) => id !== contact.id)
        : [...prev, contact.id],
    );
  };

  useEffect(() => {
    if (contacts.length === 0) return;
    const emails = contacts.map((c) => c.email).filter(Boolean);
    if (emails.length === 0) return;

    apiService
      .post<{ existing_emails: string[] }>(
        "/api/contacts/contact/check-existing/",
        { emails },
      )
      .then((res) => {
        setExistingEmails(new Set(res.data.existing_emails));
      })
      .catch(() => {});
  }, [contacts]);

  const handleConvertToContacts = async () => {
    const selectedContacts = filtered.filter((c) => checkedIds.includes(c.id));

    if (selectedContacts.length === 0) {
      showErrorToast("Please select contacts to convert");
      return;
    }

    const payload = {
      stream_id: streamId,
      comments: selectedContacts.map((c) => ({
        author: c.name,
        email: c.email || null,
        phone: c.phone || null,
      })),
    };

    setIsConverting(true);
    try {
      const response = await apiService.post<{
        created: number;
        skipped: number;
        errors: string[];
      }>("/api/contacts/contact/bulk-create-from-comments/", payload);

      showSuccessToast(
        `${response.data.created} contact(s) created, ${response.data.skipped} skipped.`,
      );
      setExistingEmails((prev) => {
        const next = new Set(prev);
        selectedContacts.forEach((c) => {
          if (c.email) next.add(c.email.toLowerCase());
        });
        return next;
      });
      setCheckedIds([]);
      // Refresh from the backend so phone-only contacts (which existingEmails
      // can't represent) also flip to "already saved" via savedContacts.
      fetchSavedContacts();
    } catch {
      showErrorToast("Failed to convert contacts.");
    } finally {
      setIsConverting(false);
    }
  };

  const allSelectable = filtered.filter((c) => !isConverted(c));

  const allSelected =
    allSelectable.length > 0 &&
    allSelectable.every((c) => checkedIds.includes(c.id));

  const toggleSelectAll = () => {
    if (allSelected) {
      setCheckedIds([]);
    } else {
      setCheckedIds(allSelectable.map((c) => c.id));
    }
  };

  const toggleThankYouMode = () => {
    setThankYouMode((prev) => !prev);
    setCheckedThankYouIds([]);
  };

  const toggleThankYouCheck = (contact: Contact) => {
    setCheckedThankYouIds((prev) =>
      prev.includes(contact.id)
        ? prev.filter((id) => id !== contact.id)
        : [...prev, contact.id],
    );
  };

  const allThankYouSelected =
    thankableFiltered.length > 0 &&
    thankableFiltered.every((c) => checkedThankYouIds.includes(c.id));

  const toggleSelectAllThankYou = () => {
    setCheckedThankYouIds(
      allThankYouSelected ? [] : thankableFiltered.map((c) => c.id),
    );
  };

  const handleBulkSendThankYou = () => {
    const selected = thankableFiltered.filter((c) =>
      checkedThankYouIds.includes(c.id),
    );
    if (selected.length === 0) {
      showErrorToast("Please select contacts to send thank-you messages.");
      return;
    }
    setBulkWhatsAppOpen(true);
  };

  return (
    <Box sx={{ width: "100%" }}>
      <Box sx={{ mb: 3 }}>
        <FormControlLabel
          control={
            <Checkbox checked={allSelected} onChange={toggleSelectAll} />
          }
          label="Select All"
        />
        <Grid container spacing={1.5} alignItems="center">
          <Grid size={{ xs: 12, sm: "auto" }} sx={{ minWidth: { sm: 200 } }}>
            <TextField
              fullWidth
              placeholder="Search here..."
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <SearchIcon
                      sx={{ fontSize: 18, color: "text.secondary" }}
                    />
                  </InputAdornment>
                ),
              }}
              sx={{
                "& .MuiOutlinedInput-root": {
                  borderRadius: "8px",
                  fontSize: "13px",
                },
              }}
            />
          </Grid>

          <Grid size={{ xs: 12, sm: "auto" }} sx={{ minWidth: { sm: 150 } }}>
            <CustomSelect
              onChange={(e) => setFilterSource(String(e.target.value))}
              label=""
              defaultValue="all"
              fullWidth
              size="small"
            >
              <MenuItem value="all">All Contacts</MenuItem>
              <MenuItem value="generic">Memorial Page</MenuItem>
              <MenuItem value="youtube">Youtube</MenuItem>
              <MenuItem value="imported">Imported</MenuItem>
              <MenuItem value="visitor">Visitors</MenuItem>
            </CustomSelect>
          </Grid>

          <Grid size={{ xs: 12, sm: "auto" }} sx={{ minWidth: { sm: 150 } }}>
            <CustomSelect
              label="Bulk Actions"
              defaultValue="email"
              fullWidth
              size="small"
              onChange={() => {}}
            >
              <MenuItem value="email">Email</MenuItem>
            </CustomSelect>
          </Grid>

          <Grid size={{ xs: 12, sm: "auto" }}>
            <Button
              variant="contained"
              fullWidth
              disabled={checkedIds.length === 0 || isConverting}
              onClick={handleConvertToContacts}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                height: 38,
                px: 3,
                whiteSpace: "nowrap",
              }}
            >
              {isConverting ? "Converting..." : "Convert to Contacts"}
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: "auto" }}>
            <Button
              variant="outlined"
              startIcon={<UploadFile />}
              onClick={() => setImportOpen(true)}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                height: 38,
                whiteSpace: "nowrap",
              }}
            >
              Import CSV
            </Button>

            {importOpen && (
              <ImportContactsModal
                setOpen={setImportOpen}
                fetchContacts={fetchSavedContacts}
                streamId={streamId}
              />
            )}
          </Grid>
          <Grid size={{ xs: 12, sm: "auto" }}>
            <Button
              variant={thankYouMode ? "contained" : "outlined"}
              color={thankYouMode ? "success" : "primary"}
              startIcon={<WhatsAppIcon />}
              onClick={toggleThankYouMode}
              sx={{
                textTransform: "none",
                fontWeight: 600,
                borderRadius: "8px",
                height: 38,
                whiteSpace: "nowrap",
              }}
            >
              {thankYouMode ? "Cancel" : "Bulk Thank You"}
            </Button>
          </Grid>
        </Grid>

        {thankYouMode && (
          <Paper
            elevation={0}
            sx={{
              position: "sticky",
              bottom: 16,
              zIndex: 10,
              mt: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: "wrap",
              gap: 1.5,
              px: 2.5,
              py: 1.5,
              borderRadius: "12px",
              bgcolor: "#f0fdf4",
              border: "1px solid #bbf7d0",
              boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
            }}
          >
            <FormControlLabel
              control={
                <Checkbox
                  checked={allThankYouSelected}
                  onChange={toggleSelectAllThankYou}
                  disabled={thankableFiltered.length === 0}
                  size="small"
                  sx={{
                    color: "#86efac",
                    "&.Mui-checked": { color: "#006838" },
                  }}
                />
              }
              label={
                <Typography
                  sx={{ color: "#1e293b", fontSize: 13.5, fontWeight: 500 }}
                >
                  Select all not-yet-thanked imports ({thankableFiltered.length}
                  )
                </Typography>
              }
              sx={{ m: 0 }}
            />

            <Stack direction="row" spacing={2} alignItems="center">
              {checkedThankYouIds.length > 0 && (
                <Typography
                  sx={{ color: "#006838", fontSize: 13, fontWeight: 600 }}
                >
                  {checkedThankYouIds.length} selected
                </Typography>
              )}

              <Button
                variant="contained"
                disableElevation
                startIcon={<WhatsAppIcon />}
                disabled={checkedThankYouIds.length === 0}
                onClick={handleBulkSendThankYou}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  height: 36,
                  bgcolor: "#25D366",
                  "&:hover": { bgcolor: "#1ebd5a" },
                  "&.Mui-disabled": { bgcolor: "#d1fae5", color: "#94a3b8" },
                }}
              >
                Send Thank You
              </Button>

              <Button
                variant="outlined"
                size="small"
                onClick={toggleThankYouMode}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  borderRadius: "8px",
                  height: 36,
                  borderColor: "#cbd5e1",
                  color: "#64748b",
                  "&:hover": { borderColor: "#94a3b8", bgcolor: "transparent" },
                }}
              >
                Cancel
              </Button>
            </Stack>

            {thankedIds.size > 0 && (
              <Typography
                component="button"
                onClick={() => {
                  if (streamId) {
                    localStorage.removeItem(`thanked-contacts-${streamId}`);
                    setThankedIds(new Set());
                    setCheckedThankYouIds([]);
                    showSuccessToast("Reset thanked status for all contacts.");
                  }
                }}
                sx={{
                  width: "100%",
                  textAlign: "right",
                  fontSize: 11,
                  color: "#94a3b8",
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  p: 0,
                  "&:hover": { color: "#64748b", textDecoration: "underline" },
                }}
              >
                Reset thanked status ({thankedIds.size})
              </Typography>
            )}
          </Paper>
        )}
      </Box>

      {filtered.length === 0 ? (
        <Box sx={{ py: 6, textAlign: "center" }}>
          <Typography sx={{ color: "text.secondary", fontSize: "13px" }}>
            No contacts found
          </Typography>
        </Box>
      ) : (
        <Grid container spacing={2}>
          {filtered.map((contact) => (
            <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={contact.id}>
              <ContactCard
                contact={contact}
                streamId={streamId}
                contactId={contact.contactId}
                backendPhone={contact.phone || undefined}
                checked={checkedIds.includes(contact.id)}
                onCheck={() => toggleCheck(contact)}
                converted={isConverted(contact)}
                onConverted={() => fetchSavedContacts()}
                onDeleted={() => fetchSavedContacts()}
                thanked={thankedIds.has(contact.id)}
                onWhatsAppClick={() => setThankYouTarget(contact)}
                thankYouMode={thankYouMode}
                thankable={isThankable(contact)}
                thankYouChecked={checkedThankYouIds.includes(contact.id)}
                onThankYouCheck={() => toggleThankYouCheck(contact)}
              />
            </Grid>
          ))}
        </Grid>
      )}

      {thankYouTarget && (
        <ContactsMemoryThankYouCard
          open={!!thankYouTarget}
          onClose={() => setThankYouTarget(null)}
          contactName={thankYouTarget.name}
          contactPhone={thankYouTarget.phone}
          deceasedName={deceasedName}
          streamLink={streamLink}
          onSent={() => {
            setThankedIds((prev) => {
              const next = new Set(prev).add(thankYouTarget.id);
              if (streamId) {
                try {
                  localStorage.setItem(
                    `thanked-contacts-${streamId}`,
                    JSON.stringify(Array.from(next)),
                  );
                } catch {
                  // non-fatal — worst case, "Thanked" won't persist this one time
                }
              }
              return next;
            });
            setThankYouTarget(null);
          }}
        />
      )}

      {bulkWhatsAppOpen && (
        <BulkWhatsAppModal
          open={bulkWhatsAppOpen}
          onClose={() => setBulkWhatsAppOpen(false)}
          contacts={thankableFiltered.filter((c) =>
            checkedThankYouIds.includes(c.id),
          )}
          deceasedName={deceasedName}
          streamLink={streamLink}
          onSent={(sentContactIds) => {
            setThankedIds((prev) => {
              const next = new Set(prev);
              sentContactIds.forEach((id) => next.add(id));
              if (streamId) {
                try {
                  localStorage.setItem(
                    `thanked-contacts-${streamId}`,
                    JSON.stringify(Array.from(next)),
                  );
                } catch {
                  // non-fatal
                }
              }
              return next;
            });
            setBulkWhatsAppOpen(false);
            setThankYouMode(false);
            setCheckedThankYouIds([]);
          }}
        />
      )}
    </Box>
  );
}
